import { parseBullet } from '@/bullet';
import { convertUnit, formatQty, formatTemp, parseQty } from '@/qty';
import type { DirectionSegment, Ingredient, IngredientRef, UnitSystem } from '@/types';

function formatScaled(qty: number, unit: string | undefined, system: UnitSystem): string {
  const { qty: cQty, unit: cUnit } = unit ? convertUnit(qty, unit, system) : { qty, unit };
  const num = formatQty(cQty, cUnit);
  return cUnit ? `${num} ${cUnit}` : num;
}

function formatAmount(
  ing: Ingredient,
  ref: IngredientRef,
  multiplier: number,
  system: UnitSystem,
): string | null {
  const m = ing.fixed ? 1 : multiplier;
  if (ref.portion && typeof ref.portion !== 'string') {
    const { quantity, unit } = ref.portion;
    return formatScaled(parseQty(quantity) * m, unit, system);
  }
  if (!(ing.quantity || ing.unit)) return null;
  const portion = ref.portion === 'half' ? 0.5 : ref.portion === 'third' ? 1 / 3 : 1;
  const scaled = parseQty(ing.quantity) * m * portion;
  return formatScaled(scaled, ing.unit, system).trim() || null;
}

function renderRef(
  seg: IngredientRef,
  ingredientsByName: Map<string, Ingredient>,
  multiplier: number,
  unitSystem: UnitSystem,
  showAmounts: boolean,
) {
  const ing = ingredientsByName.get(seg.ref);
  if (!ing) return <>{seg.ref}</>;
  const amount = showAmounts ? formatAmount(ing, seg, multiplier, unitSystem) : null;
  return (
    <>
      {ing.recipe ? <a href={`/r/${ing.recipe}`}>{ing.name}</a> : ing.name}
      {amount ? <span class="amount"> ({amount})</span> : null}
    </>
  );
}

function renderSegment(
  seg: DirectionSegment,
  ingredientsByName: Map<string, Ingredient>,
  multiplier: number,
  unitSystem: UnitSystem,
  showAmounts: boolean,
  vesselTexts: Record<string, string> | undefined,
) {
  if (typeof seg === 'string') return seg;
  if ('temp' in seg) return formatTemp(seg.temp, seg.unit, unitSystem);
  if ('vessel' in seg) return vesselTexts?.[seg.key] ?? '(vessel)';
  if ('quantity' in seg) {
    const m = seg.fixed ? 1 : multiplier;
    return formatScaled(parseQty(seg.quantity) * m, seg.unit, unitSystem);
  }
  return renderRef(seg, ingredientsByName, multiplier, unitSystem, showAmounts);
}

interface Props {
  bullet: string;
  ingredientsByName: Map<string, Ingredient>;
  multiplier: number;
  unitSystem: UnitSystem;
  showAmounts: boolean;
  vesselTexts?: Record<string, string> | undefined;
}

export function DirectionSegments({
  bullet,
  ingredientsByName,
  multiplier,
  unitSystem,
  showAmounts,
  vesselTexts,
}: Props) {
  return (
    <>
      {parseBullet(bullet).map((seg, i) => (
        <span key={i}>
          {renderSegment(seg, ingredientsByName, multiplier, unitSystem, showAmounts, vesselTexts)}
        </span>
      ))}
    </>
  );
}
